// Push-pin button used to pin jobs, so the List and the Map focus on them
import clsx from 'clsx';

// Material Symbols "push_pin" (filled and outlined), Apache License 2.0
const PIN_FILLED_PATH =
  'M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z';
const PIN_OUTLINED_PATH =
  'M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4m3-2H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1c.55 0 1-.45 1-1s-.45-1-1-1z';

/**
 * Push-pin icon, tilted like a pin stuck in a board.
 * @param {object} props
 * @param {boolean} [props.isFilled]
 * @param {string} [props.className]
 * @returns {React.JSX.Element}
 */
export function PinIcon({
  isFilled = true,
  className,
}: {
  isFilled?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={clsx('size-4 shrink-0 rotate-45 fill-current', className)}
      aria-hidden="true"
    >
      <path d={isFilled ? PIN_FILLED_PATH : PIN_OUTLINED_PATH} />
    </svg>
  );
}

/**
 * Toggle button with a push-pin icon: filled when pinned, outlined when not.
 * Stops the click from reaching parent click handlers (e.g. a job card that
 * selects the job for editing).
 * @param {object} props
 * @param {boolean} props.isPinned
 * @param {() => void} props.onToggle
 * @param {string} props.label - What is pinned, for the accessible name (e.g. the job name).
 * @param {string} [props.color] - Icon color; defaults to the text color.
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @returns {React.JSX.Element}
 */
export default function PinToggle({
  isPinned,
  onToggle,
  label,
  color,
  disabled,
  className,
}: {
  isPinned: boolean;
  onToggle: () => void;
  label: string;
  color?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={clsx(
        'pin-toggle btn btn-ghost btn-xs btn-square shrink-0',
        'disabled:border-transparent disabled:bg-transparent disabled:text-current disabled:opacity-30',
        !isPinned && 'opacity-70 hover:opacity-100',
        className
      )}
      style={color ? { color } : undefined}
      aria-pressed={isPinned}
      aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${label}`}
      title={
        isPinned
          ? 'Unpin this job'
          : 'Pin this job to focus the map and the list on it'
      }
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <PinIcon isFilled={isPinned} />
    </button>
  );
}
